<template>
  <DashboardPanelTemplate
    :panel-size="panelSize"
    :panel-focus="panelFocus"
    :loading="loading || initialRequestLoading"
    :error="loadingError"
    :error-message="$t('ErrorMessage')"
  >
    <div class="fulfillment-metrics-header">
      <div class="title-wrapper">
        <h4 v-t="'Title'" />
      </div>
      <div class="dropdown-filters">
        <sb-dropdown
          v-model="timeFilter"
          :disabled="requestLoading"
          class="filter filter-time"
        >
          <sb-selected-option slot="selection" slot-scope="{ selected }">
            {{ getSelectedLabel(timeFilterOptions, selected) }}
          </sb-selected-option>
          <sb-options>
            <sb-option v-for="option of timeFilterOptions" :key="option.value" :value="option">
              {{ option.label }}
            </sb-option>
          </sb-options>
        </sb-dropdown>

        <sb-dropdown
          v-model="currentMetric"
          :disabled="requestLoading"
          class="filter filter-shipping-method"
          :options="fulfillmentMetrics"
        >
          <sb-selected-option slot="selection" slot-scope="{ selected }">
            {{ getShippingMethodLabel(selected) }}
          </sb-selected-option>
          <sb-options>
            <sb-option
              slot-scope="{ option }"
              :value="option"
            >
              {{ option.shippingMethod }}
            </sb-option>
          </sb-options>
        </sb-dropdown>
      </div>
    </div>
    <div class="fulfillment-metrics-container">
      <div class="fulfillment-metrics-value-wrapper">
        <div class="fulfillment-metric-value">
          {{ getIntegerDisplay(currentMetric.shippedCount) }}
        </div>
        <div v-t="'OrdersDelivered'" class="fulfillment-metric-label" />
      </div>
      <div v-if="currentMetric.AverageShippingCost && !isPoweredBySubAccountUser" class="fulfillment-metrics-value-divisor" />
      <div v-if="currentMetric.AverageShippingCost && !isPoweredBySubAccountUser" class="fulfillment-metrics-value-wrapper avg-fulfillment-cost">
        <div v-if="showLocalCurrencyWithSymbolAndCode" class="fulfillment-metric-value">
          {{
            currentMetric.AverageShippingCost | formatCurrency({currency: homeCountry.currencyCode, showCurrencyCode: false, minimumFractionDigits: 2})
          }}
        </div>
        <div v-else class="fulfillment-metric-value">
          {{ getMoneyDisplay(currentMetric.AverageShippingCost) }}
        </div>
        <div class="fulfillment-metric-label">
          <span v-t="'AvgFulfillmentCost'" /><span v-if="showLocalCurrencyWithSymbolAndCode" class="fulfillment-currency-code">&nbsp;({{ homeCountry.currencyCode }})</span>
        </div>
      </div>
      <div class="fulfillment-metrics-value-divisor fulfillment-metrics-value-middle-divisor" />
      <div class="fulfillment-metrics-value-wrapper">
        <div class="fulfillment-metric-value">
          {{ getDaysDisplay(currentMetric.averageDaysToFulfill) }}
        </div>
        <div v-t="'AvgTimeToFulfill'" class="fulfillment-metric-label" />
      </div>
      <div class="fulfillment-metrics-value-divisor" />
      <div class="fulfillment-metrics-value-wrapper">
        <div class="fulfillment-metric-value">
          {{ getDaysInTransitDisplay(currentMetric.averageDaysInTransit) }}
        </div>
        <div v-t="'AvgTimeInTransit'" class="fulfillment-metric-label" />
      </div>
    </div>
  </DashboardPanelTemplate>
</template>
<script>
import DashboardPanelTemplate from '@/components/layouts/DashboardPanelTemplate'
import TimeFilterEnum from '@/enums/modules/dashboard/fulfillmentMetricsTimeFilter'
import FulfillmentMetricsMessages from '@/components/dashboard/panels/FulfillmentMetricsPanel.messages'
import ShipbobAppSettings from '../../../appSettings/appSetting'
import MerchantCountryCurrency from '@/models/pricing/merchantCountryCurrency'
import { getAuthService } from '@/auth/auth.service'
export default {
  name: 'FulfillmentMetricsPanel',
  inject: ['isPoweredBySubAccountUser'],
  components: {
    DashboardPanelTemplate
  },
  props: {
    panelSize: {
      type: String,
      required: true
    },
    panelFocus: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      initialRequestLoading: true,
      requestLoading: true,
      datastoreLocation: 'dashboard/fulfillmentMetrics/',
      analyticsDashboardUrl: '',
      homeCountry: new MerchantCountryCurrency()
    }
  },
  computed: {
    fulfillmentMetrics () {
      return this.$shipBobStore.getters[this.datastoreLocation + 'fulfillmentMetrics']
    },
    filters () {
      return this.$shipBobStore.getters[this.datastoreLocation + 'filters']
    },
    loadingError () {
      return this.$shipBobStore.getters[this.datastoreLocation + 'loadingError']
    },
    timeFilter: {
      get () {
        return this.filters.timeFilter
      },
      set (selection) {
        this.$shipBobStore.dispatch(this.datastoreLocation + 'setFilter', { filter: 'timeFilter', value: selection.value })
        this.refreshMetrics()
      }
    },
    currentMetric: {
      get () {
        return this.$shipBobStore.getters[this.datastoreLocation + 'currentMetric']
      },
      set (selection) {
        this.$shipBobStore.dispatch(this.datastoreLocation + 'setCurrentMetric', selection)
      }
    },
    timeFilterOptions () {
      return this.mapEnumToDropdown(TimeFilterEnum)
    },
    showLocalCurrencyWithSymbolAndCode () {
      return this.$shipBobStore.getters.showLocalCurrencyWithSymbolAndCode
    }
  },
  async created () {
    const token = await getAuthService().getToken()
    this.analyticsDashboardUrl = `${ShipbobAppSettings.analyticsDashboardUrl}?token=${token}}`
    this.homeCountry = this.$shipBobStore.getters['pricing/userHomeCountry']
  },
  async mounted () {
    this.initialRequestLoading = false
    this.$shipBobStore.getters.enableLocalCurrencyOnDashboardPage === true
      ? await this.$shipBobStore.dispatch(this.datastoreLocation + 'getFulfillmentMetricsByCurrency', this.$shipBobStore.getters['pricing/userHomeCountry']?.currencyCode)
      : await this.$shipBobStore.dispatch(this.datastoreLocation + 'getFulfillmentMetrics')
    this.requestLoading = false
  },
  methods: {
    getSelectedLabel (options, value) {
      const option = options.find(x => x.value === value)
      return (option !== undefined) ? option.label : ''
    },
    getShippingMethodLabel (selected) {
      if (selected && selected.shippingMethod) return selected.shippingMethod
      else return this.$t('ShippingMethod')
    },
    mapEnumToDropdown (enumToMap, messages) {
      return Object.keys(enumToMap).map(x => {
        return { label: this.$t(enumToMap[x].message), value: enumToMap[x].value }
      })
    },
    getDaysInTransitDisplay (value) {
      if (this.timeFilter === TimeFilterEnum.Yesterday.value) {
        return '-'
      } else return (this.getDaysDisplay(value))
    },
    getIntegerDisplay (value) {
      if (this.requestLoading) return '-'
      return value ? value.toFixed(0) : '0'
    },
    getDaysDisplay (value) {
      if (this.requestLoading) return '-'
      return value ? value.toFixed(1) : '0.0'
    },
    getMoneyDisplay (value) {
      if (this.requestLoading) return '-'
      return value ? '$' + value.toFixed(2) : '$0.00'
    },
    async refreshMetrics () {
      this.requestLoading = true
      this.$shipBobStore.getters.enableLocalCurrencyOnDashboardPage === true
        ? await this.$shipBobStore.dispatch(this.datastoreLocation + 'getFulfillmentMetricsByCurrency', this.$shipBobStore.getters['pricing/userHomeCountry']?.currencyCode)
        : await this.$shipBobStore.dispatch(this.datastoreLocation + 'getFulfillmentMetrics')
      this.requestLoading = false
    }
  },
  i18n: { messages: FulfillmentMetricsMessages }
}
</script>

<style lang="scss" scoped>
  @import '@/../assets/scss/shipbob-custom/components/dashboard/_panel-fulfillment-metrics.scss';
</style>
